import React from "react";
import {FontAwesomeIcon} from "@fortawesome/react-fontawesome";
import AccountSelector from "../../Components/AccountSelector/AccountSelector";
import ModuleSelector from "../../Components/ModuleSelector/ModuleSelector";
import {graphqlGated} from "../../../Utils/GraphqlGate";
import {getModuleHistoryForSerial, getModulesHistoryForAccount} from "../../../graphql/queries";
import GraphQlTool from "../../../Utils/GraphQlTool";
import {I18n} from "aws-amplify/utils";

let instanceHardwareHistory;

const HistoryMode = {
    MODULES: "Modules",
    ACCOUNTS: "Accounts",
};

/**
 * @typedef {Object} HardwareHistoryProps
 * @property {import('../../../Models/DomainTypes').PayStation[]} payStations
 * @property {{ uuid: string, accountId: number, accountLocale: string }} currentUser Dashboard user returned by the API.
 * @property {{ graphql: Function }} API
 * @property {boolean} isDarkStyle
 * @property {boolean} isDesktop
 * @property {string} locale
 */

/**
 * Consulte l'historique des modules par module ou par compte.
 * @extends {React.Component<HardwareHistoryProps>}
 */
class HardwareHistory extends React.Component {
    constructor( props ) {
        super( props );
        instanceHardwareHistory = this;
        this.state = {
            mode: HistoryMode.MODULES,
            historyRecords: []
        };
    }

    /** @param {number} accountId */
    fetchHardwareForAccount( accountId ) {
        if ( instanceHardwareHistory.props.API ) {
            graphqlGated( instanceHardwareHistory.props.API,
                {
                    query: getModulesHistoryForAccount,
                    variables: {
                        accountId: accountId,
                        cognitoId: instanceHardwareHistory.props.currentUser.uuid
                    }
                } )
                .then( returned => {
                    const tool = new GraphQlTool('getModulesHistoryForAccount', returned);
                    const history = tool.extract();
                    instanceHardwareHistory.setState({
                        historyRecords: history.records
                    });
                })
                .catch( error => {
                    console.error( error );
                });
        }
        //console.log( 'Fetch for accountId' , accountId );
    }

    /** @param {string} deviceName */
    fetchHardwareForSerial( deviceName ) {
        if ( instanceHardwareHistory.props.API ) {
            graphqlGated( instanceHardwareHistory.props.API,
                {
                    query: getModuleHistoryForSerial,
                    variables: {
                        deviceName: deviceName,
                        cognitoId: instanceHardwareHistory.props.currentUser.uuid
                    }
                } )
                .then( returned => {
                    const tool = new GraphQlTool('getModuleHistoryForSerial', returned);
                    const history = tool.extract();
                    instanceHardwareHistory.setState({
                        historyRecords: history
                    });
                })
                .catch( error => {
                    console.error( error );
                });
        }
        //console.log( 'Fetch for cbms' , deviceName );
    }

    onFilterChanges( event , value ) {
        if( instanceHardwareHistory.state.mode === HistoryMode.MODULES ) {
            instanceHardwareHistory.fetchHardwareForSerial( value.name );
        } else if ( instanceHardwareHistory.state.mode === HistoryMode.ACCOUNTS ) {
            instanceHardwareHistory.fetchHardwareForAccount( value );
        }
    };

    clearFilters() {
        this.setState({});
    }

    extractModules() {
        const modules = [];
        this.props.payStations.forEach( payStation => {
            payStation.devices.forEach( device => {
                modules.push({id: device.id, name: device.name });
            })
        })
        return modules;
    }

    /** @param {string} isoDateString */
    localizeDate( isoDateString ) {
        return new Date( new Date( isoDateString).getTime() - ( new Date().getTimezoneOffset() * 60 * 1000 ) );
    }

    /** @param {string} isoDateString */
    formatDate( isoDateString ) {
        return `${instanceHardwareHistory.localizeDate( isoDateString ).toLocaleDateString( instanceHardwareHistory.props.currentUser.accountLocale )} ${instanceHardwareHistory.localizeDate( isoDateString ).toLocaleTimeString( instanceHardwareHistory.props.currentUser.accountLocale )}`
    }

    formatHistoryForModule() {
        const formatted = [];
        const byOpenThenEndedDesc = ( a , b ) => {
            if ( a['is_open'] !== b['is_open'] ) return a['is_open'] ? -1 : 1;

            const endA = a.endedAt == null ? Number.POSITIVE_INFINITY : new Date( a.endedAt ).getTime();
            const endB = b.endedAt == null ? Number.POSITIVE_INFINITY : new Date( b.endedAt ).getTime();
            if ( endA !== endB ) return endB - endA;

            const startA = new Date( a.startedAt ).getTime();
            const startB = new Date( b.startedAt ).getTime();
            if ( startA !== startB ) return startB - startA;

            return 0;
        };

        let currentAccount = null;
        instanceHardwareHistory.state.historyRecords
            .sort( byOpenThenEndedDesc )
            .forEach( record  => {
                if( currentAccount !== record.account ) {
                    formatted.push( record );
                    currentAccount = record.account;
                } else {
                   formatted[ formatted.length - 1 ].startedAt = formatted[ formatted.length - 1 ].startedAt <= record.startedAt ? formatted[ formatted.length - 1 ].startedAt : record.startedAt;
                }
            } );
        return formatted;
    }

    formatHistoryForAccount() {
        const formatted = [];

        const cmpStr = ( A, B ) => {
            const a = ( A ?? '' ).toString();
            const b = ( B ?? '' ).toString();
            return a.localeCompare( b, undefined, {sensitivity: 'base'} );
        };

        const byOpenThenEndedDesc = ( a , b ) => {
            // 2) box ASC
            const cBox = cmpStr( a.box, b.box );
            if ( cBox !== 0 ) return cBox;

            // 3) device ASC
            const cDev = cmpStr( a.device, b.device );
            if ( cDev !== 0 ) return cDev;

            if ( a['is_open'] !== b['is_open'] ) return a['is_open'] ? -1 : 1;

            const endA = a.endedAt == null ? Number.POSITIVE_INFINITY : new Date( a.endedAt ).getTime();
            const endB = b.endedAt == null ? Number.POSITIVE_INFINITY : new Date( b.endedAt ).getTime();
            if ( endA !== endB ) return endB - endA;

            const startA = new Date( a.startedAt ).getTime();
            const startB = new Date( b.startedAt ).getTime();
            if ( startA !== startB ) return startB - startA;

            return 0;
        };

        let currentAccount = null;
        let currentBox = null;
        let currentDevice = null;
        instanceHardwareHistory.state.historyRecords
            .sort( byOpenThenEndedDesc )
            .forEach( record  => {
                if( currentAccount !== record.account ||
                    currentBox !== record.box ||
                    currentDevice !== record.device ) {
                    formatted.push( record );
                    currentAccount = record.account;
                    currentBox = record.box;
                    currentDevice = record.device;
                } else {
                    formatted[ formatted.length - 1 ].startedAt = formatted[ formatted.length - 1 ].startedAt <= record.startedAt ? formatted[ formatted.length - 1 ].startedAt : record.startedAt;
                }
            } );
        return formatted;
    }

    /** @param {import('../../../Models/DomainTypes').Box | null | undefined} box */
    extractBoxLabel( box ) {
        console.log( 'box' , box );
        if( box?.name ) {
            const customName = box?.informations?.attributes?.name;
            return ( customName ) ? customName : box?.name;
        }
        return I18n.get("error");
    }

    /** @param {import('../../../Models/DomainTypes').Box | null | undefined} box */
    extractBoxExtraLabel( box ) {
        return ( box?.extraData ) ? box.extraData : "";
    }

    renderModeSelector() {
        return (
            <div className="history-mode-selector">
                <div className={`by-module-selector selector-btn ${(this.state.mode === HistoryMode.MODULES)?'selected':''}`}
                     onClick={() => {instanceHardwareHistory.setState({mode: HistoryMode.MODULES});}}>
                    <FontAwesomeIcon icon={["fas", "money-bill-wave"]} />
                </div>
                <div className={`by-account-selector selector-btn ${(this.state.mode === HistoryMode.ACCOUNTS)?'selected':''}`}
                     onClick={() => {instanceHardwareHistory.setState({mode: HistoryMode.ACCOUNTS});}}>
                    <FontAwesomeIcon icon={["fas", "users"]} />
                </div>
            </div>
        );
    }

    renderSearchBar() {
        if( this.state.mode === HistoryMode.MODULES ) {
            const modules = this.extractModules();
            return (
                <div className="history-search-bar">
                    <ModuleSelector  modules={modules}
                                     payStations={this.props.payStations}
                                     user={this.props.currentUser}
                                     API={this.props.API}
                                     showIcon={true}
                                     isDarkStyle={this.props.isDarkStyle}
                                     onChange={this.onFilterChanges} />
                </div>
            );
        } else if ( this.state.mode === HistoryMode.ACCOUNTS ) {
            return (
                <div className="history-search-bar">
                    <AccountSelector user={this.props.currentUser}
                                     API={this.props.API}
                                     showIcon={true}
                                     isDarkStyle={this.props.isDarkStyle}
                                     onChange={this.onFilterChanges} />
                </div>
            );
        }
        return null;
    }

    renderContent() {
        if( this.state.mode === HistoryMode.MODULES ) {
            return this.renderModuleResults();
        } else if ( this.state.mode === HistoryMode.ACCOUNTS ) {
            return this.renderAccountResults();
        }
        return null;
    }

    renderModuleResults() {
        if( instanceHardwareHistory.state.historyRecords.length > 0 ) {
            const history = instanceHardwareHistory.formatHistoryForModule();
            return (
                <div className={`history-results ${(instanceHardwareHistory.props.isDarkStyle) ? 'dark': ''}`}>
                    <div className="history-results-line" key={`line-header`}>
                        <div className="account">{I18n.get('Account')}</div>
                        <div className="startedAt">{I18n.get('StartedAt')}</div>
                        <div className="endedAt">{I18n.get('EndedAt')}</div>
                    </div>
                    {history.map( candidate =>
                        <div className="history-results-line" key={`line-${instanceHardwareHistory.state.historyRecords.indexOf(candidate)}`}>
                            <div className="account">{candidate.account}</div>
                            <div className="startedAt">{instanceHardwareHistory.formatDate( candidate['startedat'] )}</div>
                            <div className="endedAt">{( candidate['endedat'] === null ) ? I18n.get('On going') : instanceHardwareHistory.formatDate( candidate['endedat'] )}</div>
                        </div>)}
                </div>
            );
        }
        return null;
    }

    renderAccountLine( candidate ) {
        const box = instanceHardwareHistory.props.payStations.filter( payStation => payStation.box.name === candidate['box'] )[0]?.box;
        if( box ) {
            return(
                <div className="history-results-line" key={`line-${instanceHardwareHistory.state.historyRecords.indexOf(candidate)}`}>
                    <div className="account">{candidate.account}</div>
                    <div className="box">
                        <div className="title">{instanceHardwareHistory.extractBoxLabel( box )}</div>
                        <div className="extra-data">{instanceHardwareHistory.extractBoxExtraLabel( box )}</div>
                    </div>
                    <div className="device">{candidate['device']}</div>
                    <div className="startedAt small">{instanceHardwareHistory.formatDate( candidate['startedat'] )}</div>
                    <div className="endedAt small">{( candidate['endedat'] === null ) ? I18n.get('On going') : instanceHardwareHistory.formatDate( candidate['endedat'] )}</div>
                </div>
            );
        }
        return null;
    }

    renderAccountResults() {
        if( instanceHardwareHistory.state.historyRecords.length > 0 ) {
            const history = instanceHardwareHistory.formatHistoryForAccount();
            console.log( 'history' , history );
            return (
                <div className={`history-results ${(instanceHardwareHistory.props.isDarkStyle) ? 'dark': ''}`}>
                    <div className="history-results-line" key={`line-header`}>
                        <div className="account">{I18n.get('Account')}</div>
                        <div className="box">{I18n.get('Box')}</div>
                        <div className="device">{I18n.get('Device')}</div>
                        <div className="startedAt small">{I18n.get('StartedAt')}</div>
                        <div className="endedAt small">{I18n.get('EndedAt')}</div>
                    </div>
                    {history.map( candidate => instanceHardwareHistory.renderAccountLine( candidate ) )}
                </div>
            );
        }
        return null;
    }

    render() {
        return (
            <React.Fragment>
                <div className={`history-root z-over fullscreen ${( this.props.isDarkStyle ) ? 'dark' : ''}`}>
                    {this.renderModeSelector()}
                    {this.renderSearchBar()}
                    {this.renderContent()}
                </div>
            </React.Fragment>
        );
    }
}

export default HardwareHistory;
